import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import heroSteak from "@/assets/hero-steak.jpg";

const HERO_VIDEOS = [
  { src: "/videos/hero-sizzle.mp4", duration: 15000 },
];

const Hero = () => {
  const [currentVideo, setCurrentVideo] = useState(0);
  const [fading, setFading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFading(true);
      setTimeout(() => {
        setCurrentVideo((prev) => (prev + 1) % HERO_VIDEOS.length);
        setFading(false);
      }, 600);
    }, HERO_VIDEOS[currentVideo].duration);
    return () => clearTimeout(timer);
  }, [currentVideo]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [currentVideo]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Video */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={heroSteak}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-600 ${fading ? "opacity-0" : "opacity-100"}`}
      >
        <source src={HERO_VIDEOS[currentVideo].src} type="video/mp4" />
      </video>
      
      {/* Overlay gradients */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-background/50 to-transparent" />

      {/* Animated ember particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-sizzle-orange rounded-full animate-float opacity-60" style={{ animationDelay: '0s' }} />
        <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-sizzle-red rounded-full animate-float opacity-50" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-sizzle-gold rounded-full animate-float opacity-40" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 right-1/4 w-1 h-1 bg-sizzle-orange rounded-full animate-float opacity-60" style={{ animationDelay: '0.5s' }} />
      </div>


      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <p className="text-sizzle-orange font-medium tracking-widest uppercase mb-4 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            Family Dining on Main GT Road
          </p>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading font-bold mb-6 glow-text">
            Sizzlers Restaurant{" "}
            <span className="text-gradient-fire">Jhelum</span>
            <span className="block text-2xl md:text-4xl lg:text-5xl mt-3 text-foreground/90 font-heading">
              BBQ, Steaks &amp; Family Dining
            </span>
          </h1>
          
          <p className="text-lg md:text-2xl text-foreground/80 mb-4 max-w-4xl mx-auto animate-fade-up" style={{ animationDelay: '0.3s' }}>
            BBQ platters, steaks, handi, burgers, desserts, and late-night family dining in Jhelum Cantt.
          </p>

          <p className="text-base md:text-lg text-muted-foreground mb-8 max-w-3xl mx-auto animate-fade-up" style={{ animationDelay: '0.35s' }}>
            Visit Sizzlers near Chawani Chowk on Main GT Road for dine-in, takeaway, reservations, and signature specials loved across Jhelum.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up" style={{ animationDelay: '0.4s' }}>
            <Button variant="hero" asChild>
              <Link to="/menu">View Menu</Link>
            </Button>
            <Button variant="heroOutline" asChild>
              <Link to="/reservations">Book a Table</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-8 h-8 text-foreground/50" />
      </div>
    </section>
  );
};

export default Hero;
